import { SIZES, SPACING } from '@alto/design-library-tokens';
import { ActionSheetProvider, LoadingEmptyState, ToastProvider, useScreenSize } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { ExperimentationListener } from '@alto/features';
import { Auth0Provider } from '@auth0/auth0-react';
import React, { useCallback, useEffect } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
// eslint-disable-next-line import/no-deprecated
import { fetchAddresses } from '~shared/actions/addresses';
import { setPostLoginRoute } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
// eslint-disable-next-line import/no-deprecated
import { fetchPrescriptions, fetchTransfers } from '~shared/actions/prescriptions';
// eslint-disable-next-line import/no-deprecated
import { fetchPrescriptionPricing } from '~shared/actions/pricing';
// eslint-disable-next-line import/no-deprecated
import { fetchShipments } from '~shared/actions/shipments';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { getAuth0ClientIDForEnv, getAuth0DomainForEnv } from '~shared/config';
import { useFetchNextAvailableDatesForCart } from '~shared/features/next-available-date/hooks/useFetchNextAvailableDatesForCart';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { queries } from '~shared/queries/query-keys';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { QueryClientProvider } from '~shared/react-query';
import { ReactQueryDevtools } from '~shared/react-query/devtools';
import { queryClient } from '~shared/react-query/queryClient';
import { Sentry } from '~shared/sentry';
import { ReduxProvider, useDispatchShared, useSelectorShared } from '~shared/store';
import { configureStore } from '~web/configureStore';
import { NavigationProvider, ThemeProvider } from '~web/contexts';
import { useBrowserTab, useGetInitialData } from '~web/hooks';
import { WebRoutes } from '~web/routes';

// avoid 'auth0-spa-js must run on a secure origin' error
// when running app locally pointed at server running locally
// we can remove this code when url for that scenario is localhost or https
if (process.env.BACKEND_ENVIRONMENT === 'local' && !window.crypto?.subtle) {
  Object.defineProperty(window, 'crypto', {
    writable: true,
    value: {
      subtle: { digest: () => 'foo' },
      getRandomValues: () => new Uint8Array(16),
    },
  });
}

type Props = {
  readonly store: any;
  readonly skipInitialDataFetch?: boolean;
};

const DataLoader = () => {
  const authorized = useSelectorShared((state) => state.auth.authorized);
  const dispatch = useDispatchShared();
  const fetchNextAvailableDatesForCart = useFetchNextAvailableDatesForCart();

  useGetInsurances({ enabled: authorized });
  useGetPaymentMethods({ enabled: authorized });

  useEffect(() => {
    if (!authorized) return;
    fetchNextAvailableDatesForCart();
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchTransfers());
    queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchShipments());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchPrescriptions(true));
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchUsers());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchAddresses());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchDeliveries());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchPrescriptionPricing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, dispatch]);

  return null;
};

const PatientDashboardApp = ({ store, skipInitialDataFetch = false }: Props) => {
  useBrowserTab();

  const { persistor } = configureStore();
  const { getInitialData } = useGetInitialData({ store });

  useEffect(() => {
    if (skipInitialDataFetch) return;
    getInitialData();
  }, [getInitialData, skipInitialDataFetch]);

  const createAnalyticsEvent = useCallback(
    (actionSheetName: string) => {
      store.dispatch(sendAnalyticsEvent(createEvent(EVENTS.ACTION_SHEET_VIEWED, { type: actionSheetName })));
    },
    [store],
  );

  const { isSMScreenOrBigger } = useScreenSize();

  return (
    <div data-testid="dashboard">
      <ThemeProvider>
        <ReduxProvider store={store}>
          <PersistGate
            loading={<LoadingEmptyState />}
            persistor={persistor}
          >
            <Experimentation.Provider>
              <Auth0Provider
                domain={getAuth0DomainForEnv()}
                clientId={getAuth0ClientIDForEnv()}
                authorizationParams={{
                  redirect_uri: window.location.origin,
                  prompt: 'login', // always prompt user for email and password
                }}
                onRedirectCallback={(appState, user) => {
                  Sentry.captureMessage('Auth0 Redirect', (scope) => {
                    scope.setLevel('info');
                    scope.setTag('auth0_id', user?.sub);
                    return scope;
                  });
                  if (appState?.next) {
                    store.dispatch(setPostLoginRoute(appState?.next));
                  }
                }}
              >
                <QueryClientProvider client={queryClient}>
                  <SafeAreaProvider>
                    <ToastProvider
                      offset={
                        (isSMScreenOrBigger
                          ? parseInt(SIZES.PAGE.WEB_NAVBAR_HEIGHT.LG.replace('px', ''))
                          : parseInt(SIZES.PAGE.WEB_NAVBAR_HEIGHT.SM.replace('px', ''))) + SPACING.STATIC.MD.value
                      }
                    >
                      <BrowserRouter>
                        <ActionSheetProvider createAnalyticsEvent={createAnalyticsEvent}>
                          <NavigationProvider>
                            <WebRoutes />
                          </NavigationProvider>
                          <ReactQueryDevtools />
                        </ActionSheetProvider>
                      </BrowserRouter>
                    </ToastProvider>
                  </SafeAreaProvider>
                  <DataLoader />
                </QueryClientProvider>
              </Auth0Provider>
            </Experimentation.Provider>
            <ExperimentationListener />
          </PersistGate>
        </ReduxProvider>
      </ThemeProvider>
    </div>
  );
};

export default Sentry.withProfiler(PatientDashboardApp);
